/**
 * Manage Budget → Meta Ads tab content (#4288 Phase 2b).
 *
 * Replaces the pre-4288 "Pilot Program — Get in Touch" placeholder in ``budget-v2.tsx`` with the
 * real per-campaign / per-ad-set surface. Each Meta campaign renders as one row:
 *
 *   - CBO (Scaling) — single slider bound to ``MetaCampaign.daily_budget_cents``. Save dispatches
 *     ``PATCH /budgets/meta/campaigns/{id}``.
 *   - ABO (Prospecting / Retargeting) — single slider bound to the campaign's (sole today)
 *     ``MetaAdSet.daily_budget_cents``. Save dispatches ``PATCH /budgets/meta/ad-sets/{id}``.
 *
 * For #4277 chunk 2 each phase-campaign ships with exactly one ad set so the row count == campaign
 * count regardless of mode. When future PRD work surfaces multi-ad-set ABO campaigns this file
 * will need an expandable-row affordance — one slider per ad set under the parent campaign.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * POLICY: BUDGET CHANGES MUST BE EXPLICITLY SAVED — NO AUTO-SAVE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Like every other budget surface in the app, edits here are staged locally and
 * persisted only when the user clicks "Save Changes" (rendered by the host,
 * ``business/budget-v2.tsx``, which owns the shared save row across tabs).
 * Auto-saving on slider release was this file's original behavior; it was
 * removed for parity with the Account Limits and Google Ads tabs — auto-save
 * surprises users into committing real money outcomes they may have only been
 * previewing, and made this the one tab with no Save button.
 *
 * The host wires ``apiRef`` (save/discard) and ``onDirtyChange`` — the same
 * contract BudgetV2 itself offers its parents. The platform-allocation cap on
 * the backend stays the authoritative gate; a rejected row surfaces via toast
 * and the row falls back to the server's value.
 */

import { Spinner } from '@/components/glitch-ui/spinner';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ArrowRight, Pause, Play } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { usePlatformConnections } from '@/hooks/usePlatformConnections';

import { Slider } from '@/components/glitch-ui/slider';
import { Card } from '@/components/ui/card';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { useToast } from '@/hooks/useToast';
import {
  metaCampaignDetailQueryOptions,
  metaCampaignsQueryOptions,
  useMetaIntegrationStatus,
  useUpdateMetaAdSetBudgetMutation,
  useUpdateMetaCampaignBudgetMutation,
} from '@/lib/query-options';
import type { MetaCampaignDetailResponse, MetaCampaignResponse, MetaCampaignType } from '@/lib/types';

import metaLogo from '/meta.png';

// Backend enum values stay TESTING/SCALING/RETARGETING (#4277 chunk 4a). Display labels are
// demo-aligned: Prospecting / High Performance / Retargeting. Mirrors the wizard's
// ``PHASE_LABELS`` map.
const PHASE_LABELS: Record<MetaCampaignType, string> = {
  TESTING: 'Prospecting',
  SCALING: 'High Performance',
  RETARGETING: 'Retargeting',
};

// Type pill palette — mirrors the Google tab's per-type pills (#5981).
const PHASE_PILL_CLASSES: Record<MetaCampaignType, string> = {
  TESTING: 'bg-blue-50 text-blue-600',
  SCALING: 'bg-purple-50 text-purple-600',
  RETARGETING: 'bg-green-50 text-green-600',
};

/**
 * First-cell status icon — the Google tab's CampaignStatusIcon mapping over Meta states (#5981):
 * generating spinner, green Play when live, amber Pause only after the campaign HAS been live,
 * amber alert for never-launched ("ready to go live" — same rule as the campaign sidebar).
 */
function MetaCampaignStatusIcon({
  status,
  hasBeenLive,
  isGenerating,
}: {
  status: MetaCampaignResponse['status'];
  hasBeenLive: boolean;
  isGenerating: boolean;
}) {
  if (isGenerating) return <Spinner size="sm" className="text-gray-400" />;
  if (status === 'ACTIVE') return <Play className="w-4 h-4 text-green-500" />;
  if (status === 'PAUSED') {
    return hasBeenLive ? (
      <Pause className="w-4 h-4 text-amber-500" />
    ) : (
      <AlertCircle className="w-4 h-4 text-amber-500" />
    );
  }
  if (status === 'DELETED') return <AlertCircle className="w-4 h-4 text-red-500" />;
  return <AlertCircle className="w-4 h-4 text-gray-400" />;
}

/** The status icon's tooltip — mirrors the Google tab's state-specific titles. */
function statusTitle(status: MetaCampaignResponse['status'], hasBeenLive: boolean, isGenerating: boolean): string {
  if (isGenerating) return 'Campaign is being created';
  if (status === 'ACTIVE') return 'Live';
  if (status === 'PAUSED') return hasBeenLive ? 'Paused' : 'Ready to go live';
  return status === 'DELETED' ? 'Deleted' : 'Archived';
}

// Sliders work in whole-currency (matches the Google tab's `budget` unit and what users actually
// type). Convert to cents on save. Cap defaults to €100/day = 10_000 cents — adjusted dynamically
// to whatever the Meta platform-cap (Account Limits → Meta) sets.
const DEFAULT_SLIDER_MAX_WHOLE = 100;

/** Imperative handle the host uses to drive the shared Save Changes row. */
export interface MetaBudgetTabApi {
  /** Persist every staged row edit. Throws on the first failure so the host's save flow can surface it. */
  save: () => Promise<void>;
  /** Drop all staged edits, restoring every row to its server value. */
  discard: () => void;
}

interface MetaBudgetTabProps {
  /** Monthly Meta platform-cap from ``Business.platform_budgets[Meta].amount``, in whole-currency. */
  metaPlatformBudgetMonthly: number;
  /** Receives the save/discard handle — the host's Save Changes button calls through this. */
  apiRef?: React.MutableRefObject<MetaBudgetTabApi | null>;
  /** Fires whenever the staged-edits state flips between clean and dirty. */
  onDirtyChange?: (isDirty: boolean) => void;
  /** Fires when the (pending-aware) allocation crosses the platform cap — drives the host's tab badge + save gate. */
  onOverAllocationChange?: (isOver: boolean) => void;
}

export function MetaBudgetTab({
  metaPlatformBudgetMonthly,
  apiRef,
  onDirtyChange,
  onOverAllocationChange,
}: MetaBudgetTabProps) {
  // These are Meta budgets, so the symbol must follow the selected Meta AD ACCOUNT's currency, not the
  // org default (a €-org can run a $-account). ``selected_ad_account.currency`` overrides the org source.
  const { data: metaStatus } = useMetaIntegrationStatus();
  const { symbol: currencySymbol } = useCurrencySymbol('org', metaStatus?.selected_ad_account?.currency);
  const cs = currencySymbol ?? '';
  const { toast } = useToast();
  const navigate = useNavigate();
  const { metaConnected, isLoading: isLoadingConnections } = usePlatformConnections();

  // Campaign list — the row identity.
  const { data: campaigns = [], isLoading: isLoadingCampaigns } = useQuery(metaCampaignsQueryOptions());

  // Per-campaign detail fetches — needed to read the ad set's ``daily_budget_cents`` for ABO rows.
  // ``useQueries`` runs them in parallel; React Query dedupes against the existing campaign-detail
  // cache so navigating to a single campaign page after this hits a warm cache.
  const detailQueries = useQueries({
    queries: campaigns.map((campaign) => ({
      ...metaCampaignDetailQueryOptions(campaign.slug),
    })),
  });

  const campaignMutation = useUpdateMetaCampaignBudgetMutation();
  const adSetMutation = useUpdateMetaAdSetBudgetMutation();
  const queryClient = useQueryClient();

  // Staged (unsaved) edits, keyed by row. A key is present only while its value differs from the
  // server's ``savedDailyCents`` — editing a row back to its saved value removes the key, so the
  // dirty state resets without a save. Persisted in one batch by ``save`` below.
  const [pendingCents, setPendingCents] = useState<Record<string, number>>({});
  // Which rows have a save in-flight — so we disable only the saving rows, not every row (the two
  // mutations are shared across rows, so their ``isPending`` can't tell us which row is saving).
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());

  const sliderMaxMonthly = Math.max(metaPlatformBudgetMonthly, DEFAULT_SLIDER_MAX_WHOLE * 30);
  const sliderMaxDaily = Math.max(1, Math.round(sliderMaxMonthly / 30));

  const rows = useMemo(
    () => buildRows(campaigns, detailQueries.map((q) => ({ data: q.data, isError: q.isError }))),
    [campaigns, detailQueries],
  );

  // Stage an edit; drop the key when the value returns to the saved one so dirty resets cleanly.
  const stageEdit = (row: CboCampaignRow | AboAdSetRow, newDailyCents: number) =>
    setPendingCents((prev) => {
      const next = { ...prev };
      if (newDailyCents === (row.savedDailyCents ?? 0)) {
        delete next[row.key];
      } else {
        next[row.key] = newDailyCents;
      }
      return next;
    });

  const changedRows = useMemo(
    () =>
      rows.filter(
        (row): row is CboCampaignRow | AboAdSetRow =>
          row.kind !== 'abo-error' &&
          pendingCents[row.key] !== undefined &&
          pendingCents[row.key] !== (row.savedDailyCents ?? 0),
      ),
    [rows, pendingCents],
  );
  const hasChanges = changedRows.length > 0;

  useEffect(() => {
    onDirtyChange?.(hasChanges);
  }, [hasChanges, onDirtyChange]);

  const save = useCallback(async () => {
    // Reductions first, so the platform-allocation total drops before increases push it up —
    // the same ordering the Google tab's batched save uses.
    const sorted = [...changedRows].sort(
      (a, b) =>
        (pendingCents[a.key] - (a.savedDailyCents ?? 0)) - (pendingCents[b.key] - (b.savedDailyCents ?? 0)),
    );
    setSavingKeys(new Set(sorted.map((row) => row.key)));
    const savedKeys: string[] = [];
    try {
      for (const row of sorted) {
        const cents = pendingCents[row.key];
        try {
          // skipInvalidate: invalidate once for the whole batch below, not 3× per row (#6163 P1-15).
          if (row.kind === 'cbo-campaign') {
            await campaignMutation.mutateAsync({ campaignId: row.campaignId, daily_budget_cents: cents, skipInvalidate: true });
          } else {
            await adSetMutation.mutateAsync({ adSetId: row.adSetId, daily_budget_cents: cents, skipInvalidate: true });
          }
          savedKeys.push(row.key);
        } catch (error) {
          toast({
            title: 'Could Not Save Budget',
            description: error instanceof Error ? error.message : 'Please try again.',
            variant: 'destructive',
          });
          throw error; // surface to the host's save flow; already-saved rows stay saved
        } finally {
          setSavingKeys((prev) => {
            const next = new Set(prev);
            next.delete(row.key);
            return next;
          });
        }
      }
    } finally {
      // One invalidation for the whole batch (#6163 P1-15) instead of 3 per row. Await the refetch
      // BEFORE dropping the staged values, so rows keep showing the new value instead of flashing the
      // stale saved one between the pending-clear and the refetch.
      if (savedKeys.length > 0) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['meta', 'campaigns'] }),
          queryClient.invalidateQueries({ queryKey: ['meta', 'campaign'] }),
          // Budget-pacing is the only dashboard rendering budgets; scoped so the batch never
          // refetches 'placement', a live Graph call per observer (#6163 P3, #6179).
          queryClient.invalidateQueries({ queryKey: ['meta', 'dashboards', 'budget-pacing'] }),
        ]);
        setPendingCents((prev) => {
          const next = { ...prev };
          savedKeys.forEach((key) => delete next[key]);
          return next;
        });
      }
      setSavingKeys(new Set());
    }
  }, [changedRows, pendingCents, campaignMutation, adSetMutation, toast, queryClient]);

  const discard = useCallback(() => setPendingCents({}), []);

  useEffect(() => {
    if (apiRef) apiRef.current = { save, discard };
    return () => {
      if (apiRef) apiRef.current = null;
    };
  }, [apiRef, save, discard]);

  // Pending-aware allocation totals — shared by the footer summary and the host's
  // over-allocation gate (tab badge + disabled Save), mirroring the Google tab's
  // ``isCampaignsOverPlatform``.
  const totalAllocatedDaily = rows.reduce(
    (sum, row) =>
      sum + (row.kind === 'abo-error' ? 0 : Math.round((pendingCents[row.key] ?? row.savedDailyCents ?? 0) / 100)),
    0,
  );
  const totalAllocatedMonthly = totalAllocatedDaily * 30;
  const isOverPlatform = metaPlatformBudgetMonthly > 0 && totalAllocatedMonthly > metaPlatformBudgetMonthly;

  useEffect(() => {
    onOverAllocationChange?.(isOverPlatform);
  }, [isOverPlatform, onOverAllocationChange]);

  if (isLoadingCampaigns || isLoadingConnections) {
    return (
      <Card className="p-12 flex items-center justify-center min-h-[320px]">
        <Spinner size="md" className="text-gray-400 mr-2" />
        <p className="text-gray-500">Loading Meta campaigns…</p>
      </Card>
    );
  }

  // Connection gate first (#6186 follow-up) — mirrors the Google sub-tab's two-stage empty state.
  // Without it a fresh org read "No Meta Campaigns Yet → Go to Campaigns", pointing at a campaigns
  // page where Meta creation cannot work before the integration is connected.
  if (!metaConnected) {
    return (
      <Card className="p-12 flex flex-col items-center text-center justify-center min-h-[320px]">
        <img src={metaLogo} className="w-10 h-auto mb-4 opacity-40" alt="Meta" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Connect Meta Ads</h3>
        <p className="text-sm text-gray-500 max-w-md mb-6">
          Connect your Meta account to start allocating budgets and managing campaigns.
        </p>
        <button
          onClick={() => navigate({ to: '/settings/account', hash: 'integrations' })}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#290BD9] hover:bg-[#2309C5] text-white text-sm font-medium rounded-md transition-colors"
        >
          Connect Account
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </Card>
    );
  }

  if (campaigns.length === 0) {
    return (
      <Card className="p-12 flex flex-col items-center text-center justify-center min-h-[320px]">
        <img src={metaLogo} className="w-10 h-auto mb-4 opacity-40" alt="Meta" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Meta Campaigns Yet</h3>
        <p className="text-sm text-gray-500 max-w-md mb-6">
          Meta campaign allocation will appear here once campaigns are created.
        </p>
        <button
          onClick={() => navigate({ to: '/campaigns' })}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#290BD9] hover:bg-[#2309C5] text-white text-sm font-medium rounded-md transition-colors"
        >
          Go to Campaigns
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <div className="grid grid-cols-[32px_minmax(150px,2.5fr)_minmax(90px,1fr)_minmax(70px,1fr)_minmax(100px,3fr)_minmax(80px,1fr)_minmax(80px,1fr)] gap-3 items-center px-4 py-2.5 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase tracking-wider">
          <div></div>
          <div>Campaign</div>
          <div>Type</div>
          <div>Mode</div>
          <div>Allocation</div>
          <div className="text-center">Daily</div>
          <div className="text-center">Monthly</div>
        </div>
        <div className="px-4">
          {rows.map((row) => {
            if (row.kind === 'abo-error') {
              // The campaign's ad-set detail failed to load; surface it inline instead of silently
              // dropping the row (which would make budget look complete when a campaign is missing).
              return (
                <div
                  key={row.key}
                  className="grid grid-cols-[32px_minmax(150px,2.5fr)_minmax(90px,1fr)_minmax(70px,1fr)_minmax(100px,3fr)_minmax(80px,1fr)_minmax(80px,1fr)] gap-3 items-center py-3 border-b border-gray-100 last:border-b-0"
                >
                  <div
                    className="flex items-center justify-center"
                    title={statusTitle(row.status, row.hasBeenLive, false)}
                  >
                    <MetaCampaignStatusIcon status={row.status} hasBeenLive={row.hasBeenLive} isGenerating={false} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{row.campaignName}</p>
                  </div>
                  <div>
                    <span className={`inline-flex items-center text-xs font-medium px-2.5 py-0.5 rounded-full ${PHASE_PILL_CLASSES[row.phase]}`}>
                      {row.phaseLabel}
                    </span>
                  </div>
                  <div>
                    <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">
                      ABO
                    </span>
                  </div>
                  <div className="col-span-3 flex items-center gap-1.5 text-[#B91C1C]">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span className="text-sm">Budget couldn't load. Reload to retry.</span>
                  </div>
                </div>
              );
            }
            const savedDailyCents = row.savedDailyCents ?? 0;
            const pending = pendingCents[row.key];
            const dailyCents = pending ?? savedDailyCents;
            const dailyWhole = Math.round(dailyCents / 100);
            const monthlyWhole = dailyWhole * 30;
            const pct = sliderMaxDaily > 0 ? Math.min((dailyWhole / sliderMaxDaily) * 100, 100) : 0;
            const isSaving = savingKeys.has(row.key);
            return (
              <div
                key={row.key}
                className="grid grid-cols-[32px_minmax(150px,2.5fr)_minmax(90px,1fr)_minmax(70px,1fr)_minmax(100px,3fr)_minmax(80px,1fr)_minmax(80px,1fr)] gap-3 items-center py-3 border-b border-gray-100 last:border-b-0"
              >
                <div
                  className="flex items-center justify-center"
                  title={statusTitle(row.status, row.hasBeenLive, row.isGeneratingDraft)}
                >
                  <MetaCampaignStatusIcon
                    status={row.status}
                    hasBeenLive={row.hasBeenLive}
                    isGenerating={row.isGeneratingDraft}
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{row.campaignName}</p>
                </div>
                <div>
                  <span className={`inline-flex items-center text-xs font-medium px-2.5 py-0.5 rounded-full ${PHASE_PILL_CLASSES[row.phase]}`}>
                    {row.phaseLabel}
                  </span>
                </div>
                <div>
                  <span
                    className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ${
                      row.kind === 'cbo-campaign' ? 'bg-purple-100 text-purple-700' : 'bg-teal-100 text-teal-700'
                    }`}
                  >
                    {row.kind === 'cbo-campaign' ? 'CBO' : 'ABO'}
                  </span>
                </div>
                <div className="px-1">
                  <Slider
                    value={pct}
                    disabled={isSaving}
                    onChange={(value) => {
                      // Active entities floor at 1 (Meta enforces a non-zero budget while serving); paused
                      // and other non-active entities may reach 0 (#5470, kept local-only by the backend).
                      const newDailyWhole = Math.max(row.allowZero ? 0 : 1, Math.round((value / 100) * sliderMaxDaily));
                      stageEdit(row, newDailyWhole * 100);
                    }}
                  />
                </div>
                <div>
                  <div className="flex items-center gap-0.5 px-3 py-1 border rounded-md w-2/3 mx-auto bg-white border-gray-300">
                    <span className="text-sm text-gray-500">{cs}</span>
                    <input
                      type="text"
                      value={dailyWhole.toLocaleString()}
                      onChange={(e) => {
                        // 0 only for non-active entities (#5470); active floors at 1, matching the backend.
                        const next = Math.max(row.allowZero ? 0 : 1, parseFloat(e.target.value.replace(/,/g, '')) || 0);
                        stageEdit(row, Math.round(next) * 100);
                      }}
                      onFocus={(e) => e.target.select()}
                      disabled={isSaving}
                      className="text-sm font-medium text-gray-900 bg-transparent border-none outline-none w-full text-right focus:ring-0 p-0 disabled:text-gray-400"
                    />
                  </div>
                </div>
                <div className="text-sm text-gray-500 text-center">
                  ~{cs}
                  {monthlyWhole.toLocaleString()}/m
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer — same "Allocated to Campaigns" summary the Google tab renders (#5981).
            Totals include staged (unsaved) edits so the remaining/over figures preview the save. */}
        {(() => {
          const isFullyAllocated = metaPlatformBudgetMonthly > 0 && totalAllocatedMonthly === metaPlatformBudgetMonthly;
          return (
            <div className={`border-t px-4 py-3 ${isOverPlatform ? 'bg-red-50' : 'bg-gray-50'}`}>
              <div className="grid grid-cols-[32px_minmax(150px,2.5fr)_minmax(90px,1fr)_minmax(70px,1fr)_minmax(100px,3fr)_minmax(80px,1fr)_minmax(80px,1fr)] gap-3 items-center">
                <div></div>
                <div>
                  <span className={`text-sm font-semibold ${isOverPlatform ? 'text-red-700' : 'text-gray-900'}`}>
                    Allocated to Campaigns
                  </span>
                  <span
                    className={`text-xs block ${isOverPlatform ? 'text-red-600' : isFullyAllocated ? 'text-green-600' : 'text-amber-600'}`}
                  >
                    {isFullyAllocated
                      ? 'Fully allocated'
                      : isOverPlatform
                        ? `${cs}${Math.round((totalAllocatedMonthly - metaPlatformBudgetMonthly) / 30).toLocaleString()}/day over budget`
                        : metaPlatformBudgetMonthly > 0
                          ? `${cs}${Math.round((metaPlatformBudgetMonthly - totalAllocatedMonthly) / 30).toLocaleString()}/day remaining`
                          : ''}
                  </span>
                </div>
                <div></div>
                <div></div>
                <div></div>
                <div>
                  <div className="flex items-center gap-0.5 px-3 py-1 border rounded-md w-2/3 mx-auto border-gray-300">
                    <span className="text-sm text-gray-500">{cs}</span>
                    <span className="text-sm font-medium text-gray-900 text-right w-full">
                      {totalAllocatedDaily.toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="text-sm text-gray-500 text-center">~{cs}{totalAllocatedMonthly.toLocaleString()}/m</div>
              </div>
            </div>
          );
        })()}
      </Card>
    </div>
  );
}

/**
 * Total Meta daily allocation in whole currency across all Meta campaigns, from the list's
 * ``effective_daily_budget_cents`` (CBO campaign budget / ABO ad-set sum — #5981). Returns
 * ``null`` while the list is loading, so callers can tell "0 allocated" from "not known yet".
 * Shared by the campaign Budget tab's "available" figure and Budget V2's first-run split.
 */
export function useMetaAllocatedDailyWhole(enabled: boolean = true): number | null {
  const { data: campaigns, isPending } = useQuery({ ...metaCampaignsQueryOptions(), enabled });
  return useMemo(() => {
    if (enabled && isPending) return null; // still loading — callers distinguish 0 from unknown
    return Math.round(
      (campaigns ?? []).reduce((sum, campaign) => sum + (campaign.effective_daily_budget_cents ?? 0), 0) / 100,
    );
  }, [campaigns, isPending, enabled]);
}

interface CboCampaignRow {
  kind: 'cbo-campaign';
  key: string;
  campaignId: number;
  campaignName: string;
  phase: MetaCampaignType;
  phaseLabel: string;
  status: MetaCampaignResponse['status'];
  hasBeenLive: boolean;
  isGeneratingDraft: boolean;
  savedDailyCents: number | null;
  // True when the entity is not ACTIVE (paused/archived) — its budget may drop to 0 (#5470). A 0 is
  // local-only: Meta rejects 0, so the backend persists it without syncing. ACTIVE entities floor at 1.
  allowZero: boolean;
}

interface AboAdSetRow {
  kind: 'abo-ad-set';
  key: string;
  campaignId: number;
  adSetId: number;
  campaignName: string;
  phase: MetaCampaignType;
  phaseLabel: string;
  status: MetaCampaignResponse['status'];
  hasBeenLive: boolean;
  isGeneratingDraft: boolean;
  savedDailyCents: number | null;
  allowZero: boolean;
}

// A placeholder row for an ABO campaign whose ad-set detail query errored — rendered as an inline
// error so the campaign stays visible instead of vanishing (#5659).
interface AboErrorRow {
  kind: 'abo-error';
  key: string;
  campaignName: string;
  phase: MetaCampaignType;
  phaseLabel: string;
  status: MetaCampaignResponse['status'];
  hasBeenLive: boolean;
}

type BudgetRow = CboCampaignRow | AboAdSetRow | AboErrorRow;

/**
 * Build one budget row per campaign. CBO campaigns map to a single ``cbo-campaign`` row; ABO
 * campaigns map to one ``abo-ad-set`` row per ad set (today: 1 per campaign).
 *
 * An ABO campaign whose detail query is still loading is skipped (it appears once the data lands);
 * one whose detail query ERRORED becomes an ``abo-error`` row so it surfaces instead of disappearing.
 */
function buildRows(
  campaigns: MetaCampaignResponse[],
  details: { data: MetaCampaignDetailResponse | undefined; isError: boolean }[],
): BudgetRow[] {
  const rows: BudgetRow[] = [];
  campaigns.forEach((campaign, idx) => {
    const phaseLabel = PHASE_LABELS[campaign.campaign_type];
    // Status-icon inputs (#5981): NULL meta_campaign_id = the creation task is still building;
    // NULL first_activated_at = never launched (sidebar's "ready to go live" rule).
    const isGeneratingDraft = campaign.meta_campaign_id == null;
    const hasBeenLive = campaign.first_activated_at != null;
    if (campaign.is_cbo) {
      rows.push({
        kind: 'cbo-campaign',
        key: `c-${campaign.id}`,
        campaignId: campaign.id,
        campaignName: campaign.name,
        phase: campaign.campaign_type,
        phaseLabel,
        status: campaign.status,
        hasBeenLive,
        isGeneratingDraft,
        savedDailyCents: campaign.daily_budget_cents ?? null,
        allowZero: campaign.status !== 'ACTIVE',
      });
      return;
    }
    const { data: detail, isError } = details[idx];
    if (isError) {
      rows.push({
        kind: 'abo-error',
        key: `e-${campaign.id}`,
        campaignName: campaign.name,
        phase: campaign.campaign_type,
        phaseLabel,
        status: campaign.status,
        hasBeenLive,
      });
      return;
    }
    if (!detail) return; // detail still loading — row will appear once it lands
    detail.ad_sets.forEach((adSet) => {
      rows.push({
        kind: 'abo-ad-set',
        key: `s-${adSet.id}`,
        campaignId: campaign.id,
        adSetId: adSet.id,
        campaignName: campaign.name,
        phase: campaign.campaign_type,
        phaseLabel,
        status: campaign.status,
        hasBeenLive,
        isGeneratingDraft,
        savedDailyCents: adSet.daily_budget_cents ?? null,
        allowZero: adSet.status !== 'ACTIVE',
      });
    });
  });
  return rows;
}
